/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import { Box, Divider, Typography } from '@mui/material'
import React from 'react'
import {
    describeDelay,
    metaStatus,
    receiverLabel,
    sendsAs,
} from '../../helpers/pipelines'
import { FONT, RADIUS, STATUS, SURFACE, TEXT } from '../../theme/tokens'
import TestSendPanel from './TestSendPanel'
import { channelMeta, SectionLabel } from './wizardShared'

const ReviewRow = ({ label, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, py: 0.75 }}>
        <Typography
            variant="overline"
            component="p"
            sx={{
                color: TEXT.muted,
                lineHeight: 1.6,
                width: 140,
                flexShrink: 0,
            }}
        >
            {label}
        </Typography>
        <Box sx={{ typography: 'body2', color: TEXT.primary }}>{children}</Box>
    </Box>
)

const OptionValue = ({ on, onLabel = 'On', offLabel = 'Off' }) => (
    <Typography
        component="span"
        variant="body2"
        sx={{
            fontWeight: 600,
            color: on ? STATUS.success.fg : TEXT.faint,
        }}
    >
        {on ? onLabel : offLabel}
    </Typography>
)

/**
 * A warning about the assigned template's Meta status: Meta will not send it,
 * or has not approved it yet. Nothing for an approved or non-WhatsApp one.
 */
const metaWarning = (template) => {
    const meta = metaStatus(template)
    if (meta?.tone === 'blocked')
        return {
            color: STATUS.error.fg,
            text: `Meta will not send this template (${meta.key.replace(
                /_/g,
                ' '
            )}). Pick another on the Templates step.`,
        }
    if (meta?.tone === 'waiting')
        return {
            color: STATUS.warning.fg,
            text: 'Meta has not approved this template yet: sends fail until it does.',
        }
    return null
}

/**
 * Step 4: read-only summary of everything about to be submitted, then an
 * optional test send per channel. `receiverOptions` names the receiver types.
 */
const StepReview = ({ form, templates, isEditMode, receiverOptions = [] }) => {
    const templateFor = (id) => templates.find((t) => t.id === id)

    return (
        <Box>
            <SectionLabel>
                {isEditMode ? 'Review changes' : 'Review & launch'}
            </SectionLabel>

            <ReviewRow label="Name">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {form.pipeline_name}
                </Typography>
            </ReviewRow>
            <ReviewRow label="Event Type">
                <Typography variant="caption" sx={{ fontFamily: FONT.mono }}>
                    {form.event_type}
                </Typography>
            </ReviewRow>
            <ReviewRow label="Schedule">
                {form.scheduleEnabled && form.scheduleSeconds > 0 ? (
                    <Typography variant="body2">
                        Delayed by {describeDelay(form.scheduleSeconds)}
                    </Typography>
                ) : (
                    <OptionValue on={false} offLabel="Immediate" />
                )}
            </ReviewRow>
            <ReviewRow label="Niceness">
                <OptionValue on={form.niceness} />
            </ReviewRow>
            <ReviewRow label="Absolute Time">
                <OptionValue on={form.absolute_time} />
            </ReviewRow>
            <ReviewRow label="One Time">
                <OptionValue
                    on={form.one_time}
                    onLabel="On: deleted with its template 14 days after creation"
                />
            </ReviewRow>
            {form.comment && (
                <ReviewRow label="Notes">
                    <Typography
                        variant="caption"
                        sx={{ color: TEXT.secondary }}
                    >
                        {form.comment}
                    </Typography>
                </ReviewRow>
            )}

            <Divider sx={{ my: 2 }} />

            <SectionLabel>Channels</SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {form.comms.map((comm) => {
                    const meta = channelMeta(comm.comm_type)
                    const template = templateFor(comm.template_id)
                    const name = template?.template_name ?? '-'
                    const sender = sendsAs(comm.comm_type, {
                        channel: comm.channel,
                        template,
                    })
                    const warning = metaWarning(template)
                    return (
                        <Box
                            key={comm.comm_type}
                            data-testid={`review-${comm.comm_type}`}
                            sx={{
                                px: 1.5,
                                py: 1.25,
                                borderRadius: `${RADIUS.md}px`,
                                border: `1px solid ${SURFACE.border}`,
                                background: SURFACE.cardHover,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        color: meta.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {meta.icon}
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        color: meta.color,
                                        width: 90,
                                        flexShrink: 0,
                                    }}
                                >
                                    {meta.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.primary,
                                        fontWeight: 500,
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    title={name}
                                >
                                    {name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: TEXT.muted }}
                                >
                                    {receiverLabel(
                                        receiverOptions,
                                        comm.receiver_type
                                    ) || '-'}
                                </Typography>
                            </Box>
                            {sender && (
                                <Typography
                                    variant="caption"
                                    component="p"
                                    sx={{
                                        color: TEXT.secondary,
                                        mt: 0.5,
                                        pl: 'calc(90px + 14px + 24px)',
                                    }}
                                >
                                    {sender}
                                </Typography>
                            )}
                            {warning && (
                                <Typography
                                    role="alert"
                                    variant="caption"
                                    component="p"
                                    sx={{
                                        color: warning.color,
                                        mt: 0.5,
                                        pl: 'calc(90px + 14px + 24px)',
                                    }}
                                >
                                    {warning.text}
                                </Typography>
                            )}
                        </Box>
                    )
                })}
            </Box>

            <Divider sx={{ my: 2 }} />

            <SectionLabel>Send a test (optional)</SectionLabel>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.muted, mb: 1.5 }}
            >
                A test sends a channel&apos;s template to the address you type
                here, as this pipeline&apos;s receiver type, and follows its
                delivery. Launching does not wait for it.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {form.comms.map((comm) => (
                    <TestSendPanel
                        key={comm.comm_type}
                        comm={comm}
                        template={templateFor(comm.template_id)}
                        receiverType={form.receiverType}
                    />
                ))}
            </Box>
        </Box>
    )
}

export default StepReview
